import yamlData from 'dropbox/server/domains.public.yaml';
import {processDomains, type ProcessedDomainsConfig} from 'js/domains/process_domains';

/**
 * Module for computing domains
 *
 * This function accepts isProd and devServerHostname rather than depending on
 * process.env and getting the hostname from the environment somehow. It is in
 * a separate file so that if we ever need to use it without having a dependency
 * on these constants, we can easily do so.
 */
type YamlType = typeof yamlData;

let lastDevServerHostname: string | null = null;
let DOMAINS: ReturnType<typeof computeDomainsNoCache> | null = null;

export function computeDomains(devServerHostname: string) {
  if (lastDevServerHostname !== devServerHostname || !DOMAINS) {
    DOMAINS = computeDomainsNoCache(devServerHostname);
    lastDevServerHostname = devServerHostname;
  }
  return DOMAINS;
}

function computeDomainsNoCache(
  devServerHostname: string
): ProcessedDomainsConfig<keyof YamlType['domains'], keyof YamlType['domain_lists']> {
  /**
   * Function that computes a large dict of different domains to use
   * for different services. The domains we use differ between dev and
   * prod so that (a) it's possible to access both at the same time (under
   * different names) and (b) it's possible to have multiple "remote" vms
   * running on vm servers, running a dropbox website accessible to everyone
   * on corp for easy bug bashing. This lends itself to several unique use
   * cases like testing mobile web on a real mobile device without having to
   * mess with proxy settings.
   *
   * devServerHostname: the name of the dev server.
   *     Usually something ended in *-{user}-coder for coder instances.
   *     If this is the empty string, we generate prod domains instead.
   */

  const useProdDomains = devServerHostname === '';
  if (!useProdDomains) {
    // Dev vm sanity check. An old configuration mistake was hostname == FQDN
    // so guard against that.  .sb is used for sandvillage, as it was easier
    // for them to route all *.sb.dev.corp.dropbox.com and similar to sand village
    // than to have separate routing for each sandvillage instance.
    if (devServerHostname.includes('.') && !devServerHostname.endsWith('.sb')) {
      throw new Error(`FQDN host names are not supported. Your hostname is '${devServerHostname}' but we only support barenames like 'dbdev' or 'dima-vm'.

If you are seeing this in a dropsuite instance, contact #dropsuite as this is likely a problem with instance provisioning.

You can find instructions on how to change your hostname here: http://askubuntu.com/questions/87665/how-do-i-change-the-hostname-without-a-restart`);
    }
  }

  return processDomains(yamlData, useProdDomains, devServerHostname);
}
